import clsx from 'clsx';
import { AcceptableProportionChartChildren, ProportionChartBarComponentType } from './types';
import { AmountDisplay } from '../AmountDisplay';
import { RecurringMonetaryAmount, MonetaryAmount } from '../../../generated/graphql';

interface ProportionChartProps {
  title?: string;
  totalTitle?: string;
  disclaimer?: string;
  className?: string;
  children?: AcceptableProportionChartChildren;
  barChartComponent?: ProportionChartBarComponentType | null;
  totalAmount: RecurringMonetaryAmount | undefined | null | MonetaryAmount;
}

export function ProportionChart({
  title,
  children,
  barChartComponent,
  totalAmount,
  className,
  disclaimer,
  totalTitle,
}: ProportionChartProps) {
  const noChildren = !children || (Array.isArray(children) && children.length === 0);

  return (
    <div className={clsx(className, '')}>
      {title && (
        <div className={clsx('w-auto flex items-center', 'h-12')}>
          <p
            className={clsx(
              'ProportionChart-Title',
              'text-darkMode-gray-medium tracking-[0.286px] leading-[normal] font-bold text-base'
            )}
          >
            {title}
          </p>
        </div>
      )}
      <div
        className={clsx(
          'ProportionChart-Container',
          'grid grid-flow-col',
          'gap-4',
          barChartComponent && 'grid-cols-[auto_1fr]'
        )}
      >
        {barChartComponent && (
          <div className={clsx('ProportionChart-BarChartComponentContainer')}>{barChartComponent}</div>
        )}
        <div className={clsx('ProportionChart-TableContainer')}>
          {children}
          <div
            className={clsx(
              'ProportionChart-TotalContainer flex flex-col justify-center items-end bg-palette-600',
              noChildren && 'rounded-t-2xl',
              'h-[68px] rounded-b-2xl pr-4'
            )}
          >
            {totalTitle && (
              <p
                className={clsx(
                  'ProportionChart-TotalTitle text-darkMode-gray-medium/60 mix-blend-darken font-medium',
                  'text-sm leading-normal'
                )}
              >
                {totalTitle}
              </p>
            )}
            <AmountDisplay
              amount={totalAmount}
              className={clsx(
                'ProportionChart-TotalAmount',
                'text-[32px] leading-[normal]',
                '[&_.AmountDisplay-Value]:text-white [&_.AmountDisplay-Value]:font-bold',
                '[&_.AmountDisplay-CurrencySign]:text-white/60 [&_.AmountDisplay-CurrencySign]:font-medium',
                '[&_.AmountDisplay-FrequencySuffix]:text-2xl',
                '[&_.AmountDisplay-FrequencySuffix]:text-white/60 [&_.AmountDisplay-FrequencySuffix]:font-medium'
              )}
            />
          </div>
        </div>
      </div>
      {disclaimer && (
        <p
          className={clsx(
            'ProportionChart-Disclaimer text-darkMode-gray-silver',
            barChartComponent ? 'ml-[63px]' : 'ml-4',
            'mt-2 leading-[13.2px] text-[11px] mr-4'
          )}
        >
          {disclaimer}
        </p>
      )}
    </div>
  );
}
